const VALID_MODAL_IDS = ['InstantTour', 'CostsAndFees', 'Contact'] as const;
type ValidModalId = (typeof VALID_MODAL_IDS)[number];
export type ModalId = ValidModalId | null;

/**
 * getModalFromQuery() modals are useful in triggering a modal on initial page load in a desktop or mobile browser.
 * getWebviewModalFromQuery() modals are used to display a modal in a webview within a ***native mobile app***.
 */

function parseModalValue(value: unknown): ValidModalId | null {
  if (typeof value === 'string' && (VALID_MODAL_IDS as ReadonlyArray<string>).includes(value)) {
    return value as ValidModalId;
  }
  return null;
}

function getBaseModalType(query: Record<string, string | boolean | undefined>, primaryParamKey: string): ModalId {
  const primaryModalValue = query[primaryParamKey];
  const modalFromPrimary = parseModalValue(primaryModalValue);

  if (modalFromPrimary) {
    return modalFromPrimary;
  }

  // If primaryParamKey was present but had an invalid string value (e.g., modal='FooBar'),
  // it should return null and not check legacy flags (as per existing test logic).
  if (typeof primaryModalValue === 'string' && !modalFromPrimary) {
    return null;
  }

  // Only check legacy flags if the primaryParamKey was not present,
  // or if it was present but not a string (e.g. a boolean).
  if (query.instantTour === true) return 'InstantTour';
  if (query.contactModal === true) return 'Contact';

  return null;
}

export function getModalFromQuery(query: Record<string, string | boolean | undefined>): ModalId {
  return getBaseModalType(query, 'modal');
}

export function getWebviewModalFromQuery(query: Record<string, string | boolean | undefined>): ModalId {
  // If webviewModal is explicitly provided, parse it
  if ('webviewModal' in query) {
    const webviewModalValue = query.webviewModal;
    const modalFromWebview = parseModalValue(webviewModalValue);

    if (modalFromWebview) {
      return modalFromWebview;
    }

    // If webviewModal was present but invalid string, return null (don't check legacy)
    if (typeof webviewModalValue === 'string' && !modalFromWebview) {
      return null;
    }

    // If webviewModal is not a string (e.g., boolean), fall through to check instantTour only
    if (query.instantTour === true) {
      return 'InstantTour';
    }

    return null;
  }

  // Check showModal parameter (used by mobile apps for webview modals)
  if ('showModal' in query) {
    const showModalValue = query.showModal;
    const modalFromShow = parseModalValue(showModalValue);

    if (modalFromShow) {
      return modalFromShow;
    }

    // If showModal was present but invalid string, return null (don't check legacy)
    if (typeof showModalValue === 'string' && !modalFromShow) {
      return null;
    }

    // If showModal is not a string (e.g., boolean), fall through to check instantTour only
    if (query.instantTour === true) {
      return 'InstantTour';
    }

    return null;
  }

  // Only instantTour=true should be treated as a webview modal
  // contactModal=true should NOT be treated as webview (it's used in email links)
  if (query.instantTour === true) {
    return 'InstantTour';
  }

  return null;
}
